import { useState, useEffect } from "react"
import {
  Button,
  Col,
  Form,
  Row,
  Modal,
  Dropdown,
  Nav,
  InputGroup,
} from "react-bootstrap"
import {
  faDownload,
  faEllipsisVertical,
  faIndianRupeeSign,
  faPaperclip,
  faPercentage,
  faPrint,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate, useParams } from "react-router-dom"
import {
  DeleteRequest,
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler"
import useLocalStorageState from "use-local-storage-state"
import { toast } from "sonner"
import { Controller, useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { baseURL, imageURL } from "../../Utils/constants/constant"
import { useQueryClient } from "react-query"
import { DetailsQuotationSkeletonLoader } from "../../Utils/skeletonLoader/DetailsQuotationSkeletonLoader"
import DeleteModal from "../DeleteModal"
import {
  downloadPdf,
  formatDate,
  formatToINR,
  printPdf,
  TextWithLineBreaks,
} from "../../Utils/helpers/commonhelper"

const schema = z.object({
  rejectionReason: z.string().optional(),
  cancellation_reason: z.string().optional(),
})
export const PurchaseDetail = () => {
  const navigate = useNavigate()
  const [customer, setCustomer] = useLocalStorageState("customer")
  const token = customer?.token?.authorization
  const rf = customer?.token?.authentication
  const queryClient = useQueryClient()

  const headers = {
    authentication: rf,
    Authorization: token,
  }
  const extras = { customer, setCustomer, rf, navigate }
  const [isLoading, setIsLoading] = useState(false)

  // state for current status update
  const [currStatus, setCurrStatus] = useState("")
  // state for purchase order id
  const [po_id, setPoId] = useState(null)

  // state for approve confirmation modal
  const [approveModal, setApproveModal] = useState(false)

  // delete modal open close state
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  // state for rejection reason modal
  const [rejectModal, setRejectModal] = useState(false)
  const [rejectionError, setRejectionError] = useState(false)
  // state for cancellation modal open and close
  const [cancellationModal, setCancellationModal] = useState(false)
  const [cancellationError, setCancellationError] = useState(false)

  // Send State
  const [sending, setSending] = useState(false)

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onSubmit",
  })

  const permission = customer.customer_details?.permission
  const isPurchase = Boolean(permission?.find(c => c.module_name?.toLowerCase()?.split(" ")?.join("_") === "purchase_order"))
  const purchasePermission = permission?.filter(c => c.module_name?.toLowerCase()?.split(" ")?.join("_") === "purchase_order")
  const purchaseGrant = {
    isDelete: customer.customer_details?.user_type !== 1 ? Boolean(Number(isPurchase && purchasePermission?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "purchase_order")?.delete)) : true,
  }
  const handleConfirmDelete = async () => {
    setIsLoading(true)
    try {
      const res = await DeleteRequest(
        `purchase/deletePurchaseOrder/${po_id}/${customer.customer_details?.id}`,
        { headers },
        extras
      )
      if (res.status === "fail" || res.success === false) {
        toast.error(res.message)
        return
      }
      setIsLoading(false)
      toast.success(res.message)
      setShowDeleteModal(false)
      navigate("/purchaseOrder")
    } catch (error) {
      console.error("error deleting purchase order")
      return
    }
  }

  const { id, module } = useParams()
  const idStrToNum = Number(id)
  const [data, setData] = useState(null)
  useEffect(() => {
    ; (async () => {
      setIsLoading(true)
      if (!idStrToNum) {
        setIsLoading(false)
        return
      }
      try {
        const res = await GetRequest(
          `purchase/getDetailsOfPOForDetailPage/${id}/${customer.customer_details?.customer_id}`,
          { headers },
          extras
        )
        if (res.status === "fail" || res.success === false) {
          return
        }
        const responseData = res.data
        const parseBillingAdd = JSON.parse(responseData.billing_address)
        const parsedShippingAdd = JSON.parse(responseData.shipping_address)
        const parsedItems = JSON.parse(responseData.items)
        let discountMoney = 0
        const specialDiscount = parsedItems[0]?.["special-discount"] || 0
        const specialDiscountType =
          parsedItems[0]?.special_discount_type || "PERCENT"
        if (specialDiscount > 0) {
          let subtotal = parsedItems[0]?.["sub-total"] || 0
          if (specialDiscountType === "PERCENT") {
            discountMoney = (subtotal / 100) * specialDiscount
          } else if (specialDiscountType === "INR") {
            discountMoney = specialDiscount
          }
        }
        const gstArray = Object.keys(parsedItems[0])
          .filter((key) => key.startsWith("gst_"))
          .map((key) => {
            return {
              gstCategory: key.split("_")[1] + "%", // take the number part and add %
              totalAmt: Number(parsedItems[0][key]),
            }
          })
        setPoId(responseData?.po_id)

        setData({
          contact_name: responseData.contact_full_name,
          po_number: responseData.po_number,
          customer_note: responseData.customer_note,
          owner_name: responseData.owner_name,
          reference_number: responseData.reference_number,
          terms_conditions: responseData.terms_condition,
          gstn_no: responseData.gstn_no,
          po_date: responseData.po_date,
          expected_delivery_date: responseData.expected_delivery_date,
          description: responseData.description,
          billing_address: parseBillingAdd,
          shipping_address: parsedShippingAdd,
          items: parsedItems[0],
          discountMoney: discountMoney,
          gst_values: gstArray,
          fileName: responseData.attachment || null,
          company_name: responseData.company_name,
        })
        setIsLoading(false)
      } catch (error) {
        console.error(error)
      }
    })()
  }, [id])

  const rejectReason = watch("rejectionReason")
  useEffect(() => {
    if (rejectReason?.trim() === "" || rejectReason === undefined) {
      setRejectionError(true)
      return
    }
    setRejectionError(false)
  }, [rejectionError, rejectReason])
  const handleApproveOrder = async (reason) => {
    if (currStatus === "reject") {
      if (rejectReason?.trim() === "" || rejectReason === undefined) return
    }
    try {
      const payload = {
        po_id: po_id,
        po_status: currStatus,
        reject_reason: currStatus === "reject" ? reason : null,
      }
      const res = await PutRequest(
        "purchase/changePOStatus",
        payload,
        { headers },
        extras
      )
      if (res.status === "fail" || res.success === false) {
        currStatus === "approved"
          ? setApproveModal(false)
          : setRejectModal(false)
        setIsLoading(false)
        return
      }
      toast.success(res.message)
      currStatus === "reject" && reset()
      currStatus === "approved" ? setApproveModal(false) : setRejectModal(false)
      queryClient.refetchQueries(["fetchPurchaseOrders"])
      navigate("/purchaseOrder")
    } catch (error) {
      console.error(error)
    }
  }

  const handleSendPurchase = async () => {
    setSending(true)
    try {
      const payload = {
        po_id: po_id,
        customer_id: customer.customer_details?.customer_id,
        user_login_id: customer.customer_details?.id,
      }
      const res = await PostRequest(
        "purchase/sendPO/",
        payload,
        false,
        { headers },
        extras
      )
      if (res.status === "fail" || res.success === false) {
        setSending(false)
        return
      }
      toast.success(res.message)
      setSending(false)
      navigate("/purchaseOrder")
    } catch (error) {
      return
    }
  }

  const handleSalesOrderDetails = async (id) => {
    // if (!id) return;
    // try {
    //     const res = await GetRequest(
    //         `/sales/fetchDetailsOfSpecificOrder/${id}`,
    //         { headers },
    //         extras
    //     );
    //     if (res.status === "success") {
    //         const editDataToSet = res.data;
    //         setEditData(editDataToSet[0]);
    //         setSalesOrderDetailsOffcanvas(true);
    //         setIsClone(false);
    //         setsalesOrderId(id);
    //     }
    // } catch (error) {
    //     console.error(error);
    // }
  }

  const handlePurchaseOrderDetails = async () => { }

  const cancelReason = watch("cancellation_reason")
  useEffect(() => {
    if (cancelReason?.trim() === "" || cancelReason === undefined) {
      setCancellationError(true)
      return
    }
    setCancellationError(false)
    navigate("/purchaseOrder")
  }, [cancellationError, cancelReason])

  const handleCancelOrder = async (reason) => {
    if (cancelReason?.trim() === "" || cancelReason === undefined) return
    try {
      const payload = {
        po_id: po_id,
        customer_id: customer.customer_details?.customer_id,
        canceletion_reason: reason,
        user_login_id: customer.customer_details?.id,
      }
      const res = await PutRequest(
        "purchase/cancelPO/",
        payload,
        { headers },
        extras
      )
      if (res.status === "fail" || res.success === false) return
      toast.success(res.message)
      reset()
      setCancellationModal(false)
    } catch (error) {
      return
    }
  }
  const getDate = (dated) => {
    const date = new Date(dated)
    let formattedDate
    if (date) {
      const day = String(date.getDate()).padStart(2, "0")
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const year = date.getFullYear()
      formattedDate = `${day}/${month}/${year}`
    }
    return formattedDate
  }

  return (
    <>
      {isLoading ? (
        <DetailsQuotationSkeletonLoader></DetailsQuotationSkeletonLoader>
      ) : !data ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "calc(100vh - 12vh)",
            background: "#fff",
            border: "1px solid lightblue",
            borderRadius: 7,
          }}
        >
          <div className="text-center">
            <h3 style={{ color: "#666", marginBottom: "1rem" }}>
              No Data Found
            </h3>
          </div>
          <Button
            className="btn btn-default"
            onClick={() => navigate("/purchaseOrder")}
          >
            Back To Purchase Order
          </Button>
        </div>
      ) : (
        <div
          style={{
            borderRadius: 7,
            background: "#fff",
            height: "calc(100vh - 12vh)",
            border: "1px solid lightblue",
            overflow: "clip",
          }}
        >
          <div
            className="header d-flex align-items-center justify-content-between mb-0 position-sticky top-0 p-3 "
            style={{
              borderBottom: "1px solid lightblue",
              zIndex: 11,
              background: "#fff",
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <h3
                className=" mb-0"
                style={{
                  whiteSpace: "nowrap",
                  fontWeight: 300,
                  fontSize: "22px",
                }}
              >
                Purchase Order Details -{" "}
                <span className="ms-1" style={{ color: "#0099ff" }}>#{data?.po_number}</span>
              </h3>
            </div>
            <div className="d-flex gap-2">
              <Dropdown as="div" className="dropdownsWithoutToggle">
                <Dropdown.Toggle as={Nav.Link} className="header-link">
                  <button
                    className="py-2 w-[30px] d-flex gap-1 rounded-1 btn-default-outline justify-content-center"
                    style={{
                      fontSize: "1rem",
                      alignItems: "center",
                      height: "39px",
                    }}
                  >
                    Pdf/Print
                  </button>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="defaultDropdown dropdown-menu-end"
                  style={{
                    width: "11rem",
                    padding: "12px 0px",
                    right: "3.4rem",
                  }}
                >
                  <Dropdown.Item
                    href="#"
                    className="d-flex align-items-center"
                    onClick={() => {
                      printPdf(
                        `${baseURL}/purchase/downloadPO/${customer?.customer_details?.customer_id}/${po_id}`,
                        headers,
                        "print"
                      )
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faPrint}
                      style={{
                        width: "16px",
                        height: "16px",
                        marginRight: "9px",
                        color: "#5e5f60",
                      }}
                    />
                    Print
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      downloadPdf(
                        `${baseURL}/purchase/downloadPO/${customer?.customer_details?.customer_id}/${po_id}`,
                        headers,
                        "download",
                        "purchase_order"
                      )
                    }}
                    className="d-flex align-items-center"
                  >
                    <FontAwesomeIcon
                      icon={faDownload}
                      style={{
                        width: "16px",
                        height: "16px",
                        marginRight: "9px",
                        color: "#5e5f60",
                      }}
                    />
                    Download
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {/* <a href={`${baseURL}/purchase/downloadPO/${customer.customer_details?.customer_id}/${po_id}/download`} className=' px-2 btn btn-default-outline'>Download PDF</a> */}
              <Button
                onClick={handleSendPurchase}
                className="btn btn-default-outline"
                disabled={sending}
              >
                {sending === true ? "Sending..." : "Send"}
              </Button>
              {module === "my_approvals" && (
                <Button
                  className="btn btn-default-outline me-2"
                  onClick={() => setCancellationModal(true)}
                >
                  {" "}
                  Cancel
                </Button>
              )}
              <Dropdown as="div" className="dropdownsWithoutToggle">
                <Dropdown.Toggle as={Nav.Link} className="header-link">
                  <button
                    className="py-2 w-[30px] d-flex gap-1 rounded-1 btn-default-outline justify-content-center"
                    style={{
                      fontSize: "1rem",
                      alignItems: "center",
                      height: "39px",
                      width: "2.5rem",
                    }}
                  >
                    <FontAwesomeIcon
                      style={{
                        width: "15px",
                        height: "15px",
                      }}
                      icon={faEllipsisVertical}
                    />
                  </button>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="dropdown-menu-end"
                  style={{ right: "3.4rem", marginTop: "4px" }}
                >
                  <Dropdown.Item
                    href="#"
                    className="d-flex align-items-center"
                    onClick={(e) => {
                      e.stopPropagation()
                      // navigate(`/createPurchaseOrder/${data?.po_id}`)
                    }}
                  >
                    {" "}
                    Edit{" "}
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#"
                    className="d-flex align-items-center"
                    onClick={(e) => {
                      if (!purchaseGrant?.isDelete) {
                        toast.warning("You are not authorized for this action")
                        return
                      }
                      e.stopPropagation()
                      setShowDeleteModal(true)
                    }}
                  >
                    {" "}
                    Delete
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <button
                className="btn btn-default"
                aria-label="Go Back"
                type="button"
                onClick={() => navigate("/purchaseOrder")}
                data-ember-action=""
                data-ember-action-668="668"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>

          <div
            className="form-container"
            style={{
              margin: 0,
              overflowY: "auto",
              height: "100%",
              paddingLeft: "0px",
              paddingTop: "12px",
              position: "relative",
            }}
          >
            <Row className="ms-0 w-100 mb-0">
              <Col md={2} style={{ borderRight: "1px solid #d4e7ed" }}>
                <div
                  style={{ position: "sticky", top: "1rem" }}
                  className=" mt-3"
                >
                  <ul className=" ps-3" style={{ listStyle: "none" }}>
                    <li className="position-relative border-start ps-4 py-2 pt-0 ">
                      <span
                        className="position-absolute  start-0 translate-middle bg-primary rounded-circle"
                        style={{
                          width: 10,
                          height: 10,
                          top: 11,
                          border: "1px solid #0099ff",
                        }}
                      ></span>
                      <a
                        href="#companyDetailsSection"
                        style={{ color: "#676262" }}
                      >
                        {" "}
                        Company Details
                      </a>
                    </li>

                    <li className="position-relative border-start ps-3 py-2 ps-4">
                      <span
                        className="position-absolute top-50 start-0 translate-middle bg-primary rounded-circle"
                        style={{ width: 10, height: 10 }}
                      ></span>
                      <a
                        href="#itemsDetailsSection"
                        style={{ color: "#676262" }}
                      >
                        {" "}
                        Items Details{" "}
                      </a>
                    </li>
                    <li className="position-relative border-start ps-3 py-2 ps-4">
                      <span
                        className="position-absolute top-50 start-0 translate-middle bg-primary rounded-circle"
                        style={{ width: 10, height: 10 }}
                      ></span>
                      <a
                        href="#termsAndConditionsSec"
                        style={{ color: "#676262" }}
                      >
                        {" "}
                        Terms & Conditions{" "}
                      </a>
                    </li>
                  </ul>
                  <hr />
                </div>
              </Col>

              <Col md={10} className="ps-3">
                <div className="ms-0 w-100" id="companyDetailsSection">
                  <div
                    className="ms-0 pt-4 px-2 w-100 mb-4"
                    style={{ backgroundColor: "#fff9fd" }}
                  >
                    <Row className="px-1">
                      <Col md={6}>
                        {" "}
                        <h3
                          // className="mb-4 pt-3"
                          style={{
                            whiteSpace: "nowrap",
                            fontWeight: 300,
                            fontSize: "19px",
                            color: "#0099ff",
                          }}
                        >
                          Customer Details
                        </h3>
                      </Col>
                      <Col md={6}>
                        {" "}
                        <div className="d-flex align-items-end justify-content-end">
                          <span className="font-small">
                            <span style={{ fontWeight: 200 }}>PO Number </span>
                            <strong>#{data.po_number}</strong>
                          </span>
                        </div>
                      </Col>
                    </Row>

                    <hr />

                    {/* details */}
                    <Row className="ms-0 w-100">
                      <Col md={6} className="ps-0 mb-4">
                        <div style={{ borderRadius: 4 }} className="ps-2">
                          {/* <h3
                            className="mb-4 pt-3"
                            style={{
                              whiteSpace: "nowrap",
                              fontWeight: 300,
                              fontSize: "19px",
                              color: "#0099ff",
                            }}
                          >
                            Quotation
                          </h3> */}
                          <Row>
                            <Col md={12}>
                              <Form.Group
                                className="mb-1"
                                controlId="formContact"
                              >
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <span
                                    style={styles.cardLabel}
                                    className="text-secondary"
                                  >
                                    Contact
                                  </span>
                                  <p
                                    className="text-primary"
                                    style={styles.cardLebelData}
                                  >
                                    <span className="pe-1 fw-semibold">:</span>
                                    {data.contact_name}
                                  </p>
                                </div>
                              </Form.Group>
                            </Col>
                            {/* <Col md={12}>
                              <Form.Group
                                className="mb-1 d-flex align-items-center gap-2"
                                controlId="formQuoteNumber"
                              >
                                <span
                                  style={{
                                    marginBottom: 1,
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    minWidth: "110px",
                                  }}
                                  className="text-secondary"
                                >
                                  Quotation Number
                                </span>

                                <p style={{ color: "black", fontWeight: 400 }}>
                                  {" "}
                                  <span className="pe-1 fw-semibold">:</span>#
                                  {quoteData.quotation_number}
                                </p>
                              </Form.Group>
                            </Col> */}
                          </Row>

                          <Row>
                            <Col md={12}>
                              {/* company name */}
                              <Form.Group
                                className="mb-1 d-flex align-items-center gap-2"
                                controlId="formCompanyName"
                              >
                                <span
                                  style={styles.cardLabel}
                                  className="text-secondary"
                                >
                                  Reference
                                </span>
                                <p style={styles.cardLebelData}>
                                  <span className="pe-1 fw-semibold">:</span>
                                  {data.reference_number|| "-"}
                                </p>
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>
                              <Form.Group
                                className="mb-1 d-flex gap-2 align-items-center"
                                controlId="formReference"
                              >
                                <span
                                  style={styles.cardLabel}
                                  className="text-secondary"
                                >
                                  Quote Date
                                </span>
                                <p style={styles.cardLebelData}>
                                  <span className="pe-1 fw-semibold">:</span>
                                  {getDate(data?.po_date)}
                                </p>
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group
                                className="mb-1 d-flex align-items-center gap-2"
                                controlId="formExpiry"
                              >
                                <span
                                  style={styles.cardLabel}
                                  className="text-secondary"
                                >
                                  Expiry Date
                                </span>
                                <p style={styles.cardLebelData}>
                                  <span className="pe-1 fw-semibold">:</span>
                                  {formatDate(data?.expected_delivery_date)}
                                </p>
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group
                                className="mb-2 d-flex gap-2 align-items-center"
                                controlId="formApprover"
                              >
                                <span
                                  style={styles.cardLabel}
                                  className="text-secondary"
                                >
                                  Lead Owner
                                </span>
                                <p style={styles.cardLebelData}>
                                  <span className="pe-1 fw-semibold">:</span>
                                  {data?.owner_name}
                                </p>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={12}>
                              <Form.Group
                                className="mb-1 mt-2 focus-z-index position-relative"
                                controlId="formDesc"
                              >
                                <p
                                  style={{
                                    minWidth: "130px",
                                    fontSize: "13px",
                                    fontWeight: 300,
                                  }}
                                  className="text-secondary"
                                >
                                  Short Description (email subject)
                                  <span className="px-1 fw-semibold">:</span>
                                </p>
                                <p
                                  style={{
                                    paddingLeft: "0px",
                                    ...styles.valuesStyle,
                                  }}
                                >
                                  {data?.description}
                                </p>
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      {/* address */}
                      <Col
                        md={6}
                        className="mb-4 d-flex flex-column ms-0 align-items-end"
                        style={{ textAlign: "end", paddingRight: "0.3rem" }}
                      >
                        {/* billing address/ */}
                        <div style={{ borderRadius: 4 }}>
                          <h3
                            className="pt-0 mb-2"
                            style={{
                              whiteSpace: "nowrap",
                              fontWeight: 300,
                              fontSize: "19px",
                              marginBottom: 14,
                              color: "#0099ff",
                            }}
                          >
                            {" "}
                            Billing Address
                          </h3>
                          <div className=" mt-0 mb-2 col-md-12">
                            <div className="w-100 mb-3">
                              <p
                                className="mb-0 fs-13 text-dark d-flex flex-column"
                                style={{ fontWeight: 500 }}
                              >
                                <p className="d-block fs-13 fw-500 text-dark">
                                  {data.company_name}
                                </p>
                                {/* <p
                                  className="mb-0 fs-14 text-dark "
                                  style={{ fontWeight: 400 }}
                                >
                                  {data?.billing_address["address"]},
                                </p> */}
                              </p>

                              <p
                                className="mb-3 fs-13 text-dark "
                                style={{ fontWeight: 300 }}
                              >
                                {data?.billing_address["address"]},
                                {data?.billing_address["city"]},{" "}
                                {data?.billing_address["state"]},{" "}
                                {data?.billing_address["country"]}-
                                {data?.billing_address["zipcode"]}{" "}
                              </p>
                            </div>
                          </div>
                        </div>
                        {/* shipping address */}
                        <div className="">
                          <h3
                            className="pt-2 mb-2"
                            style={{
                              whiteSpace: "nowrap",
                              fontWeight: 300,
                              fontSize: "19px",
                              marginBottom: 14,
                              color: "#0099ff",
                            }}
                          >
                            Shipping Address
                          </h3>

                          <p
                            className="mb-0 fs-13 text-dark "
                            style={{ fontWeight: 500 }}
                          >
                            <p className="d-block fs-13 fw-500 text-dark">
                              {data.company_name}
                            </p>
                            {/* {data?.shipping_address["address"]}, */}
                          </p>

                          <p
                            className="mb-3 fs-13 text-dark "
                            style={{ fontWeight: 300 }}
                          >
                            {data?.shipping_address["address"]}
                            {data?.shipping_address["city"]}
                            {data?.shipping_address["state"]},{" "}
                            {data?.shipping_address["country"]}-
                            {data?.shipping_address["zipcode"]}{" "}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <Row
                    id="itemsDetailsSection"
                    style={{ width: "100%", marginLeft: 0, paddingRight: 4 }}
                  >
                    <h3
                      className="ps-0 mb-3"
                      style={{
                        whiteSpace: "nowrap",
                        fontWeight: 300,
                        fontSize: "19px",
                        color: "#0099ff",
                      }}
                    >
                      {" "}
                      Items Details
                    </h3>

                    <Col md={12} className="pe-0 ps-0">
                      <div className="table-responsive quatationsTables mb-3 ">
                        <table className="table mb-0">
                          <thead>
                            <tr>
                              <th className="text-start">Items Name</th>
                              <th style={{ width: "10%" }} className="text-end">
                                Quantity
                              </th>
                              <th style={{ width: "11%" }} className="text-end">
                                Rate{" "}
                              </th>
                              <th style={{ width: "11%" }} className="text-end">
                                {" "}
                                GST(
                                <i className="bi bi-percent"></i>){" "}
                              </th>
                              <th style={{ width: "16%" }} className="text-end">
                                {" "}
                                Discount(
                                <i className="bi bi-percent"></i>/
                                <i className="ti ti-currency-rupee"></i>)
                              </th>
                              <th
                                style={{ width: "11%" }}
                                className="text-center"
                              >
                                Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {data?.items?.itemDetails.map((item, index) => (
                              <tr key={index}>
                                <td className="text-start">
                                  <div>
                                    <p
                                      className="w-100 mb-0 d-flex fs-15 align-items-center justify-content-between"
                                      style={{ fontWeight: 500 }}
                                    >
                                      {item?.name}
                                    </p>
                                    <p
                                      className="mb-0"
                                      style={{
                                        cursor: "pointer",
                                        textTransform: "capitalize",
                                      }}
                                    >
                                      {item?.description}
                                    </p>
                                  </div>
                                </td>
                                <td className="text-end">
                                  <span
                                    style={styles.valuesStyle}
                                    className="me-2 pt-0"
                                  >
                                    {item?.quantity}
                                  </span>
                                </td>
                                <td className="text-end">
                                  <span
                                    style={styles.valuesStyle}
                                    className="me-2 pt-0"
                                  >
                                    {formatToINR(item?.Rate)}
                                  </span>
                                </td>
                                <td className="text-end">
                                  <span
                                    style={styles.valuesStyle}
                                    className="me-3 pt-0"
                                  >
                                    {item?.gst}
                                    <i className="bi bi-percent"></i>
                                  </span>
                                </td>
                                <td className="text-end">
                                  <span
                                    style={styles.valuesStyle}
                                    className="me-3 pt-0"
                                  >
                                    {item?.discout}
                                    <i
                                      className={`${item?.discountType === "PERCENT"
                                        ? "bi bi-percent"
                                        : "ti ti-currency-rupee"
                                        }`}
                                    ></i>
                                  </span>
                                </td>
                                <td className="text-end">
                                  <div className="d-flex gap-2 align-items-center justify-content-center">
                                    <div
                                      className="fs-14 text-end"
                                      style={{ fontWeight: 500, fontSize: 14, paddingRight: "7px", }}
                                    >
                                      {/* <FontAwesomeIcon
                                        icon={faIndianRupeeSign}
                                      />{" "} */}
                                      {formatToINR(item?.amount)}
                                      {/* {Number(item?.amount)?.toLocaleString()} */}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}

                            <tr>
                              <th
                                className="text-start bg-transparent border-0 vertical-top"
                                style={{ verticalAlign: "top", width: "100%" }}
                              >
                                <div className="mt-1">
                                  <p
                                    className="ps-1 mb-2 fs-14"
                                    style={{ fontWeight: 400 }}
                                  >
                                    Customer Note
                                  </p>
                                  <p className=" ps-1 text-wrap fs-13 fw-normal">
                                    {data?.customer_note}
                                  </p>
                                </div>
                              </th>
                              <th
                                colSpan={5}
                                className="text-start bg-transparent border-0 "
                              >
                                <div
                                  className="w-100 h-100"
                                  style={{
                                    borderRadius: 5,
                                    background: "#f9f9fb",
                                    padding: "1rem 0.6rem",
                                  }}
                                >
                                  <div className="row sub-total mb-2 text-semibold">
                                    <div className="total-label fs-14 fw-500 col-md-6 d-flex align-items-center">
                                      Sub Total
                                    </div>
                                    <div className="total-amount text-end fs-14 fw-500 col-md-6 d-flex align-items-center  gap-1 justify-content-end">
                                      {formatToINR(data?.items["sub-total"])}
                                    </div>
                                  </div>

                                  <div
                                    className="row sub-total mb-1"
                                    style={{ fontWeight: 400 }}
                                  >
                                    <div className="total-label col-md-4 d-flex align-items-center  fs-13 text-secondary fw-400">
                                      Special Discount
                                      <span className="ps-1 fs-13 text-secondary fw-400">
                                        (
                                        {data?.items[
                                          "special_discount_type"
                                        ] === "PERCENT" ? (
                                          /* If Percentage: Show Amount then Icon */
                                          <>
                                            {data?.items["special-discount"]}
                                            <i className="bi bi-percent"></i>
                                          </>
                                        ) : (
                                          /* If Currency (Rupee): Show Icon then Amount */
                                          <>
                                            <i className="ti ti-currency-rupee"></i>
                                            {data?.items["special-discount"]}
                                          </>
                                        )}
                                        )
                                      </span>
                                    </div>
                                    {/* <div className="total-amount text-end col-md-5 justify-content-center d-flex gap-1"></div> */}

                                    <div className="total-amount text-end fs-13   text-secondary fw-400 col-md-8 d-flex align-items-center justify-content-end">
                                      {formatToINR(data?.discountMoney)}
                                    </div>
                                  </div>

                                  {data?.gst_values?.map((category) => (
                                    <div
                                      className="row sub-total mb-1"
                                      style={{ fontWeight: 400 }}
                                      key={category.gstCategory}
                                    >
                                      <div className="total-label col-md-6 d-flex align-items-center fs-13 text-secondary fw-400">
                                        GST
                                        <span className="ps-1 fs-13 text-secondary fw-400">
                                          ({category.gstCategory})
                                        </span>
                                      </div>
                                      <div className="total-amount text-end fs-13  text-secondary fw-400 col-md-6 d-flex align-items-center justify-content-end">
                                        {formatToINR(
                                          category.totalAmt?.toFixed(2)
                                        )}
                                      </div>
                                    </div>
                                  ))}

                                  <hr />
                                  <div className="row sub-total">
                                    <div className="total-label fs-14 fw-500 col-md-6">
                                      Grand Total
                                      {/* (
                                      <FontAwesomeIcon
                                        icon={faIndianRupeeSign}
                                        className="fs-13"
                                      />
                                      ) */}
                                    </div>
                                    <div className="total-amount text-end fs-14  fw-500  col-md-6">
                                      {formatToINR(data?.items["grand-total"])}
                                    </div>
                                  </div>
                                </div>
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </Col>
                  </Row>

                  <Row id="termsAndConditionsSec">
                    <Col md={8}>
                      <h3
                        className=" mb-3"
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: 300,
                          fontSize: "19px",
                          color: "#0099ff",
                        }}
                      >
                        Terms & Conditions
                      </h3>

                      <Form.Group className="mb-3" controlId="formCompanyName">
                        {/* <p className='ps-1 mb-2 fs-13' style={{fontWeight: 400}}>
                                                    Terms & Conditions
                                                </p> */}

                        {/* <div>
                          <ul style={{ fontSize: 14 }}>
                            <li>{data?.terms_conditions}</li>
                          </ul>
                        </div> */}
                        <p
                          className="ps-1 mb-2 fs-13"
                          style={{ fontWeight: 400 }}
                        >
                          {data?.terms_conditions
                            ? TextWithLineBreaks(data?.terms_conditions)
                            : "Terms and conditions not available"}
                        </p>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <h3
                        className=" mb-3"
                        style={{
                          whiteSpace: "nowrap",
                          fontWeight: 300,
                          fontSize: "19px",
                          color: "#0099ff",
                        }}
                      >
                        {" "}
                        Attachment
                      </h3>
                      <div
                        style={{
                          borderRadius: "10px",
                          padding: "10px 0px",
                          textAlign: "start",
                          width: "100%",
                          maxWidth: "400px",
                          margin: "0px",
                        }}
                      >
                        <p
                          style={{ marginBottom: "5px", color: "#666" }}
                          className="d-flex align-items-center"
                        >
                          <FontAwesomeIcon
                            icon={faPaperclip}
                            className="me-2 fs-20"
                            style={{ color: "#0099ff" }}
                          />{" "}
                          {data?.fileName ? (
                            <a
                              href={`${imageURL}${data?.fileName}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#0099ff",
                                textDecoration: "underline",
                              }}
                            >
                              {data?.fileName.split("/").pop()}
                            </a>
                          ) : (
                            "No attachment"
                          )}
                        </p>
                      </div>
                    </Col>
                  </Row>

                  <p style={{ height: "5rem" }} className="mt-2">
                    <div style={{ height: "15%" }}>
                      <div className="alert alert-warning mb-5">
                        <div className="d-flex" style={{ gap: "10px" }}>
                          <div>
                            <i
                              class="bi bi-info-circle"
                              style={{ fontSize: "25px" }}
                            ></i>
                          </div>

                          <div>
                            <p>
                              Customize your settings and form feilds from
                              setting ---&gt; Preference{" "}
                            </p>
                            <p></p>
                            <p>
                              Choose your Customise Quatation format from
                              setting ---&gt; Masters --&gt; Templates
                            </p>
                            <p></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </p>
                  <hr />
                </div>

                <p style={{ height: "8rem" }}></p>

                {module === "my_approvals" && (
                  <div
                    className="bg-white position-sticky "
                    style={{
                      // paddingRight: "17px",
                      // height: 75,
                      // bottom: "1.5px",
                      // right: "20.5px",
                      // display: "flex",
                      // alignItems: "center",
                      // justifyContent: "end",
                      // width: "93.4%",
                      // zIndex: 11,
                      // borderTop: "1px solid lightblue",
                      paddingTop: "13px",
                      paddingBottom: "15px",
                      paddingRight: "17px",
                      // height: 75,
                      bottom: "67.5px",
                      // right: "20.5px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "end",
                      // width: "93.4%",
                      zIndex: 11,
                      borderTop: "1px solid lightblue",
                    }}
                  >
                    <>
                      <Button
                        className="btn btn-default-outline me-2"
                        onClick={() => navigate("/purchaseOrder")}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="btn btn-primary me-2 rounded"
                        onClick={() => {
                          setCurrStatus("reject");
                          setRejectModal(true);
                        }}
                      >
                        {" "}
                        Reject
                      </Button>
                      <Button
                        className="btn btn-default"
                        onClick={() => {
                          setCurrStatus("approved");
                          setApproveModal(true);
                        }}
                      >
                        {" "}
                        Approve
                      </Button>
                    </>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </div>
      )}
      {approveModal && (
        <Modal
          show={approveModal}
          centered
          onHide={() => setApproveModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Purchase Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are You Sure You Want To Approve This Purchase Order ?
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn btn-default-outline"
              onClick={() => setApproveModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-default"
              onClick={() => {
                handleApproveOrder()
              }}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {rejectModal && (
        <Modal centered show={rejectModal} onHide={() => setRejectModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Reject Purchase Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Label>
              Rejection Reason <span className="text-danger">*</span>{" "}
            </Form.Label>
            <Controller
              name="rejectionReason"
              control={control}
              render={({ field }) => (
                <InputGroup hasValidation>
                  <Form.Control
                    {...field}
                    as="textarea"
                    rows={4}
                    placeholder="Please provide a detailed reason for rejection"
                  />
                </InputGroup>
              )}
            />
            {rejectionError && (
              <p
                style={{
                  fontSize: "12px",
                  color: "red",
                  marginTop: "0.5rem",
                  marginBottom: "0px",
                }}
              >
                Rejection Reason is Required !
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn btn-default-outline"
              onClick={() => setRejectModal(false)}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-default"
              onClick={handleSubmit((data) => {
                handleApproveOrder(data.rejectionReason)
              })}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {cancellationModal && (
        <Modal
          centered
          show={cancellationModal}
          onHide={() => setCancellationModal(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>Cancel Purchase Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Controller
              name="cancellation_reason"
              control={control}
              render={({ field }) => (
                <InputGroup hasValidation>
                  <Form.Control
                    {...field}
                    as="textarea"
                    rows={4}
                    placeholder="Please provide a detailed reason for cancellation"
                    isInvalid={!!errors.cancellation_reason}
                  />
                </InputGroup>
              )}
            />

            {cancellationError && (
              <p
                style={{
                  fontSize: "12px",
                  color: "red",
                  marginTop: "0.5rem",
                  marginBottom: "0px",
                }}
              >
                Cancellation Reason is Required !
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              className="btn btn-default-outline"
              onClick={(e) => {
                e.preventDefault()
                setCancellationModal(false)
              }}
            >
              Cancel
            </Button>
            <Button
              className="btn btn-default"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit((data) => {
                  handleCancelOrder(data.cancellation_reason);
                })();
              }}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      <DeleteModal
        show={showDeleteModal}
        onClose={setShowDeleteModal}
        onConfirm={handleConfirmDelete}
      ></DeleteModal>
    </>
  )
}

const styles = {
  valuesStyle: {
    marginBottom: 0,
    paddingTop: 4,
    paddingLeft: 0,
    fontSize: "0.87rem",
    lineHeight: 1.6,
    borderRadius: "0.35rem",
    fontWeight: 400,
    textTransform: "capitalize",
    color: "black",
  },
  valuesStyleForPdf: {
    marginBottom: 0,
    paddingTop: 4,
    paddingLeft: 0,
    fontSize: "0.75rem",
    lineHeight: 1.6,
    borderRadius: "0.35rem",
    fontWeight: 400,
    color: "black",
  },
  valuesStyle1: {
    marginBottom: 0,
    paddingTop: 25,
    paddingLeft: 13,
    fontSize: "0.87rem",
    lineHeight: 1.6,
    borderRadius: "0.35rem",
    fontWeight: 500,
    color: "black",
    cursor: "pointer",
  },
  lableText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    lineHeight: "normal",
    fontSize: "13px",
    fontWeight: 300,
  },
  cardDiv: {
    border: "1px solid lightblue",
    borderRadius: "3px",
    padding: "18px 18px",
    lineHeight: "0.6rem",
  },
  cardLabel: {
    marginBottom: 1,
    fontSize: "13px",
    fontWeight: 300,
    minWidth: "115px",
  },
  cardLebelData: {
    textTransform: "capitalize",
    color: "black",
    fontWeight: 400,
    fontSize: "13px",
    minWidth: "130px",
  },
};
